import { yupResolver } from '@hookform/resolvers/yup';
import React, { useRef, useState, useEffect } from 'react';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import * as yup from 'yup';
import Button from '../../../components/Button';
import { Container, Form, Row, Column } from '../../../components/commons';
import Input from '../../../components/Input';
import Modal from '../../../components/Modals/Modal';
import useToast from '../../../hooks/useToast';
import { useAutoFillRules, useCreateAutoFillRule, useUpdateAutoFillRule } from '../../../services/autoFillRules.service';
import { useApiIntegrations } from '../../../services/apiIntegrations.service';
import useDocumentTypes from '../../../services/documentTypes.service';
import { useApiDatas } from '../../../services/apiDatas.service';
import { theme } from '../../../styles/theme';
import Select from 'react-select';
import { useWatch } from 'react-hook-form';

export interface ModalHandle {
  openModal: (documentTypeId: string) => void;
}

const schema = yup.object().shape({
  steps: yup.array().of(
    yup.object().shape({
      stepOrder: yup.number().required(),
      apiIntegration: yup.string().required('Selecione uma integração API'),
      searchCriteria: yup.array().of(
        yup.object().shape({
          apiField: yup.string().required('Campo obrigatório'),
          previousStepExtractField: yup.string().optional(),
        })
      ).min(1, 'Adicione pelo menos 1 critério de busca'),
      fieldMappings: yup.array().of(
        yup.object().shape({
          apiField: yup.string().required('Campo da API obrigatório'),
          documentIndex: yup.string().required('Índice do documento obrigatório'),
        })
      ),
    })
  ),
});

const customStyles = {
  control: (base: any) => ({
    ...base,
    color: '#333',
  }),
  singleValue: (base: any) => ({
    ...base,
    color: '#333',
  }),
  input: (base: any) => ({
    ...base,
    color: '#333',
  }),
  option: (base: any, state: any) => ({
    ...base,
    color: state.isSelected ? '#fff' : '#333',
  }),
  placeholder: (base: any) => ({
    ...base,
    color: '#666',
  }),
};

const AutoFillRulesModal: React.ForwardRefRenderFunction<ModalHandle, {}> = ({}, ref) => {
  const [documentTypeId, setDocumentTypeId] = useState<string | null>(null);
  const toast = useToast();
  const modalRef = useRef<any>(null);

  const { data: rules } = useAutoFillRules();
  const { data: apiIntegrations } = useApiIntegrations(1, undefined, 1000);
  const { data: documentTypesResponse } = useDocumentTypes(1, 10000, undefined, 'name');
  const createMutation = useCreateAutoFillRule();
  const updateMutation = useUpdateAutoFillRule();

  const currentDocumentType = documentTypesResponse?.data?.find((d: any) => d._id === documentTypeId);
  const documentIndexOptions = currentDocumentType?.metadata?.map((m: any) => ({ value: m.name, label: m.name })) || [];

  const currentRule = rules?.data?.find((r: any) => r.documentType?._id === documentTypeId || r.documentType === documentTypeId);

  const { control, handleSubmit, reset } = useForm<any>({
    resolver: yupResolver(schema) as any,
    defaultValues: { steps: [] },
  });

  const { fields: steps, append: appendStep, remove: removeStep } = useFieldArray({
    control,
    name: 'steps',
  });

  React.useImperativeHandle(ref, () => ({
    openModal(docTypeId: string) {
      setDocumentTypeId(docTypeId);
      modalRef.current?.openModal();
    },
  }));

  useEffect(() => {
    if (currentRule && documentTypeId) {
      reset({ steps: currentRule.steps });
    } else {
      reset({ steps: [] });
    }
  }, [currentRule, documentTypeId, reset]);

  const onSubmit = async (formData: any) => {
    try {
      const payload = {
        documentType: documentTypeId,
        steps: formData.steps,
      };

      if (currentRule?._id) {
        await updateMutation.mutateAsync({ id: currentRule._id, payload });
      } else {
        await createMutation.mutateAsync(payload);
      }
      toast.successToast('Regras salvas com sucesso!');
      modalRef.current?.closeModal();
    } catch (error) {
      toast.errorToast('Erro ao salvar regras.');
    }
  };

  const integrationOptions = apiIntegrations?.data?.map((api: any) => ({
    value: api._id,
    label: api.name,
  })) || [];

  return (
    <Modal title='Regras de Indexação Automática' ref={modalRef} maxWidth={1200}>
      <Container style={{ maxHeight: '75vh', overflowY: 'auto', paddingRight: '10px', width: 1100 }}>
        <Form onSubmit={handleSubmit(onSubmit)}>
          {(!currentRule || steps.length === 0) && rules?.data?.length > 0 && (
            <div style={{ marginBottom: 20, padding: 15, background: '#f5f5f5', borderRadius: 8 }}>
              <label style={{ display: 'block', fontSize: 14, fontWeight: 'bold', marginBottom: 10 }}>Copiar Regra de Outro Documento (Template)</label>
              <Select
                options={rules.data.map((r: any) => ({ value: r._id, label: r.documentType?.name || 'Regra Sem Nome', rule: r }))}
                onChange={(val: any) => {
                   if (val && val.rule) {
                     reset({ steps: val.rule.steps });
                     toast.successToast('Regra copiada! Verifique os passos abaixo e altere as integrações alvo se necessário.');
                   }
                }}
                placeholder="Selecione um tipo de documento para copiar o mapeamento..."
                styles={customStyles}
              />
            </div>
          )}

          <div style={{ marginBottom: 20 }}>
            <Button
              type='button'
              label='Adicionar Passo'
              color='primary'
              variantType='outline'
              onClick={() => appendStep({ stepOrder: steps.length + 1, apiIntegration: '', searchCriteria: [{ apiField: '', previousStepExtractField: '' }], fieldMappings: [] })}
            />
          </div>

          {steps.map((step, index) => (
            <AutoFillStep 
              key={step.id} 
              step={step} 
              index={index} 
              control={control} 
              removeStep={removeStep} 
              integrationOptions={integrationOptions}
              documentIndexOptions={documentIndexOptions} 
              customStyles={customStyles} 
            />
          ))}
          <div style={{ marginTop: 20 }}>
            <Button type='submit' label='Salvar Regras' variantType='block' color='success' />
          </div>
        </Form>
      </Container>
    </Modal>
  );
};

const AutoFillStep = ({ step, index, control, removeStep, integrationOptions, documentIndexOptions, customStyles }: any) => {
  const integrationId = useWatch({
    control,
    name: `steps.${index}.apiIntegration`
  });

  const { data: apiDatasResponse } = useApiDatas(integrationId || null, 1);
  const sampleData = apiDatasResponse?.data?.[0]?.data || {};
  const apiFieldOptions = Object.keys(sampleData).map(key => ({ value: key, label: key }));

  return (
    <div style={{ border: '1px solid #ccc', padding: 15, marginBottom: 15, borderRadius: 5 }}>
      <h4 style={{ marginBottom: 10 }}>Passo {index + 1}</h4>
      
      <Row style={{ marginBottom: 15 }}>
        <Column style={{ flex: 1 }}>
          <label style={{ display: 'block', fontSize: 14, fontWeight: 'bold', marginBottom: 5 }}>Integração API Alvo</label>
          <Controller
            control={control}
            name={`steps.${index}.apiIntegration`}
            render={({ field: { onChange, value } }) => (
              <Select
                options={integrationOptions}
                value={integrationOptions.find((opt: any) => opt.value === value || opt.value === value?._id) || null}
                onChange={(val: any) => onChange(val.value)}
                placeholder="Selecione..."
                styles={customStyles}
              />
            )}
          />
        </Column>
      </Row>

      <div style={{ marginTop: 15, marginBottom: 15, padding: 10, backgroundColor: '#fafafa', border: '1px dashed #ccc', borderRadius: 4 }}>
        <h5 style={{ marginBottom: 10 }}>Critérios de Busca</h5>
        <AutoFillSearchCriteria control={control} stepIndex={index} apiFieldOptions={apiFieldOptions} customStyles={customStyles} />
      </div>

      <div style={{ marginTop: 15 }}>
        <h5 style={{ marginBottom: 10 }}>De-Para (Documento -&gt; API)</h5>
        <AutoFillFieldMappings control={control} stepIndex={index} documentIndexOptions={documentIndexOptions} apiFieldOptions={apiFieldOptions} customStyles={customStyles} />
      </div>

      <div style={{ marginTop: 10 }}>
        <Button type='button' color='error' variantType='outline' label='Remover Passo' onClick={() => removeStep(index)} />
      </div>
    </div>
  );
};

const AutoFillSearchCriteria = ({ control, stepIndex, apiFieldOptions, customStyles }: any) => {
  const { fields, append, remove } = useFieldArray({
    control,
    name: `steps.${stepIndex}.searchCriteria`,
  });

  return (
    <div>
      {fields.map((criteria, idx) => (
        <Row key={criteria.id} style={{ marginBottom: 5 }}>
          <Column style={{ flex: 1, paddingRight: 5 }}>
            <label style={{ display: 'block', fontSize: 12 }}>Campo na API</label>
            <Controller
              control={control}
              name={`steps.${stepIndex}.searchCriteria.${idx}.apiField`}
              render={({ field: { onChange, value } }) => (
                <Select
                  options={apiFieldOptions}
                  value={apiFieldOptions.find((opt: any) => opt.value === value) || (value ? { value, label: value } : null)}
                  onChange={(val: any) => onChange(val?.value || '')}
                  placeholder="Ex: numero_empenho"
                  isClearable
                  styles={customStyles}
                />
              )}
            />
          </Column>
          {stepIndex > 0 && (
            <Column style={{ flex: 1, paddingRight: 5 }}>
              <label style={{ display: 'block', fontSize: 12 }}>Valor (Passo Anterior)</label>
              <Controller
                control={control}
                name={`steps.${stepIndex}.searchCriteria.${idx}.previousStepExtractField`}
                render={({ field: { onChange, value } }) => (
                  <Select
                    options={apiFieldOptions}
                    value={apiFieldOptions.find((opt: any) => opt.value === value) || (value ? { value, label: value } : null)}
                    onChange={(val: any) => onChange(val?.value || '')}
                    placeholder="Ex: numero_empenho"
                    isClearable
                    styles={customStyles}
                  />
                )}
              />
            </Column>
          )}
          <Column style={{ flex: '0 0 40px', display: 'flex', alignItems: 'flex-end' }}>
            <Button type='button' color='error' variantType='outline' onClick={() => remove(idx)} label='X' />
          </Column>
        </Row>
      ))}
      <div style={{ marginTop: 10 }}>
        <Button
          type='button'
          color='primary'
          variantType='outline'
          label='+ Critério'
          onClick={() => append({ apiField: '', previousStepExtractField: '' })}
        />
      </div>
    </div>
  );
};

const AutoFillFieldMappings = ({ control, stepIndex, documentIndexOptions, apiFieldOptions, customStyles }: any) => {
  const { fields, append, remove } = useFieldArray({
    control,
    name: `steps.${stepIndex}.fieldMappings`,
  });

  return (
    <div>
      {fields.map((mapping, idx) => (
        <Row key={mapping.id} style={{ marginBottom: 5 }}>
          <Column style={{ flex: 1, paddingRight: 5 }}>
            <Controller
              control={control}
              name={`steps.${stepIndex}.fieldMappings.${idx}.documentIndex`}
              render={({ field: { onChange, value } }) => (
                <Select
                  options={documentIndexOptions}
                  value={documentIndexOptions.find((opt: any) => opt.value === value) || (value ? { value, label: value } : null)}
                  onChange={(val: any) => onChange(val?.value || '')}
                  placeholder="Índice do Documento..."
                  isClearable
                  styles={customStyles}
                />
              )}
            />
          </Column>
          <Column style={{ flex: 1, paddingRight: 5 }}>
            <Controller
              control={control}
              name={`steps.${stepIndex}.fieldMappings.${idx}.apiField`}
              render={({ field: { onChange, value } }) => (
                <Select
                  options={apiFieldOptions}
                  value={apiFieldOptions.find((opt: any) => opt.value === value) || (value ? { value, label: value } : null)}
                  onChange={(val: any) => onChange(val?.value || '')}
                  placeholder="Campo na API..."
                  isClearable
                  styles={customStyles}
                />
              )}
            />
          </Column>
          <Column style={{ width: 50 }}>
            <Button type='button' color='error' variantType='outline' label='X' onClick={() => remove(idx)} />
          </Column>
        </Row>
      ))}
      <div style={{ marginTop: 5 }}>
        <Button type='button' label='+ Mapeamento' color='primary' variantType='outline' onClick={() => append({ apiField: '', documentIndex: '' })} />
      </div>
    </div>
  );
};

export default React.forwardRef(AutoFillRulesModal);
